import { useState, useEffect, useMemo } from 'react';
import { useUpdateLedgerValue, useDeleteLedgerValue } from '@/api/hooks/useLedgerValues';
import { InlineEditRow } from '@/components/InlineEditRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Input } from '@/components/ui/Input';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import type { LedgerValue } from '@/api/types';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits, minorUnitsToDecimal } from '@/utils/money';

interface ValueRowProps {
    /** The ledger value to display/edit. */
    value: LedgerValue;
    /** The ledger ID this value belongs to. */
    ledgerId: number;
    /** Whether this row is currently in edit mode. */
    isEditing: boolean;
    /** Callback when edit button is clicked. */
    onEdit: () => void;
    /** Callback when editing is cancelled. */
    onCancelEdit: () => void;
    /** Callback when save is complete. */
    onSaveComplete: () => void;
    /** Whether this value can be deleted. */
    canDelete: boolean;
    /** Whether this is the first (most recent) value. */
    isFirst?: boolean;
    /** Label for the entity type (e.g., "value" or "balance"). */
    entityLabel?: string;
}

/**
 * Inline editable row for a ledger value.
 * View mode: displays date, formatted amount with currency, edit button.
 * Edit mode: date picker, amount input, currency select, save/cancel/delete buttons.
 * Includes delete confirmation dialog to prevent accidental deletions.
 */
export function ValueRow({
    value,
    ledgerId,
    isEditing,
    onEdit,
    onCancelEdit,
    onSaveComplete,
    canDelete,
    isFirst = false,
    entityLabel = 'value',
}: ValueRowProps) {
    // Capitalize first letter for display
    const EntityLabel = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
    const updateValue = useUpdateLedgerValue(ledgerId);
    const deleteValue = useDeleteLedgerValue(ledgerId);

    // Delete confirmation dialog state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Edit form state
    const [editDate, setEditDate] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editCurrency, setEditCurrency] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const originalFractionDigits = useCurrencyFractionDigits(value.originalCurrency);
    const editFractionDigits = useCurrencyFractionDigits(editCurrency);

    // Initialize form values when entering edit mode
    useEffect(() => {
        if (isEditing) {
            // Use dateFormatted (Y-m-d) directly for the date input
            setEditDate(value.dateFormatted);
            setEditAmount(minorUnitsToDecimal(value.originalValue, originalFractionDigits));
            setEditCurrency(value.originalCurrency);
            setErrors({});
        }
    }, [isEditing, originalFractionDigits, value]);

    // Format the date for display
    const formattedDate = useMemo(() => {
        const date = new Date(value.date);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }, [value.date]);

    const handleSave = async () => {
        setErrors({});

        // Validation
        const newErrors: Record<string, string> = {};
        if (!editDate) {
            newErrors.date = 'Date is required';
        }
        if (!editAmount.trim()) {
            newErrors.amount = 'Amount is required';
        }
        if (!editCurrency) {
            newErrors.currency = 'Currency is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let newAmountInMinor: number;
        try {
            newAmountInMinor = decimalToMinorUnits(editAmount, editFractionDigits);
        } catch (error) {
            setErrors({ amount: error instanceof Error ? error.message : 'Invalid amount' });
            return;
        }

        try {
            const updates: { date?: string; value?: number; currency?: string } = {};

            // Check what changed - compare against dateFormatted (Y-m-d)
            if (editDate !== value.dateFormatted) {
                updates.date = editDate;
            }
            const currencyChanged = editCurrency !== value.originalCurrency;
            if (newAmountInMinor !== value.originalValue || currencyChanged) {
                updates.value = newAmountInMinor;
            }
            if (currencyChanged) {
                updates.currency = editCurrency;
            }

            if (Object.keys(updates).length === 0) {
                // Nothing changed
                onCancelEdit();
                return;
            }

            await updateValue.mutateAsync({
                valueId: value.id,
                data: updates,
            });

            toast.success(`${EntityLabel} updated successfully`);
            onSaveComplete();
        } catch (error) {
            const isTransient = error instanceof ApiError && error.isTransient;
            toast.error(`Failed to update ${entityLabel}.${isTransient ? ' Please try again.' : ''}`);
            console.error(`Failed to update ${entityLabel}:`, error);
        }
    };

    const handleDeleteClick = () => {
        // Show confirmation dialog
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteValue.mutateAsync(value.id);
            toast.success(`${EntityLabel} deleted successfully`);
            setShowDeleteConfirm(false);
            onSaveComplete();
        } catch (error) {
            if (error instanceof Error && error.message.includes('last value')) {
                toast.error(`Cannot delete the last ${entityLabel}`);
            } else {
                const isTransient = error instanceof ApiError && error.isTransient;
                toast.error(`Failed to delete ${entityLabel}.${isTransient ? ' Please try again.' : ''}`);
            }
            console.error(`Failed to delete ${entityLabel}:`, error);
            setShowDeleteConfirm(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    // View content
    const viewContent = (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{formattedDate}</span>
                {isFirst && (
                    <span className="text-xs bg-accent/10 text-accent dark:bg-primary-light/10 dark:text-primary-light px-2 py-0.5 rounded-full">
                        Latest
                    </span>
                )}
            </div>
            <MoneyDisplay
                value={value.originalValueDecimal}
                currency={value.originalCurrency}
                fractionDigits={originalFractionDigits}
                size="md"
                className="font-medium text-gray-900 dark:text-white shrink-0"
            />
        </div>
    );

    // Edit content
    const editContent = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
                label="Date"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                error={errors.date}
                required
            />
            <MoneyInput
                label="Amount"
                value={editAmount}
                currency={editCurrency}
                onValueChange={setEditAmount}
                onCurrencyChange={setEditCurrency}
                error={errors.amount || errors.currency}
                placeholder="0.00"
            />
        </div>
    );

    return (
        <>
            <InlineEditRow
                isEditing={isEditing}
                onEdit={onEdit}
                onSave={handleSave}
                onCancel={onCancelEdit}
                onDelete={handleDeleteClick}
                viewContent={viewContent}
                editContent={editContent}
                editTitle={`Edit ${entityLabel}`}
                isSaving={updateValue.isPending}
                isDeleting={deleteValue.isPending}
                deleteDisabled={!canDelete}
                deleteDisabledReason={`Cannot delete the last ${entityLabel}`}
            />

            <DeleteConfirmDialog
                open={showDeleteConfirm}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={`Delete ${EntityLabel}`}
                description={`Are you sure you want to delete the ${entityLabel} from ${formattedDate}? This action cannot be undone.`}
                confirmLabel={`Delete ${EntityLabel}`}
                isLoading={deleteValue.isPending}
            />
        </>
    );
}
